"use client";
import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { Search, PackageSearch } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import ShopProductCard, { ShopProduct } from "../ShopProductCard";
import { WhatsAppLink } from "../StoreLinks";
import { searchProductsGA } from "../../../../lib/analytics";
import {
  categoryPath,
  formatCategory,
  productMatchesSearch,
} from "../../../../lib/catalog";

interface Props {
  initialProducts: ShopProduct[];
  initialCategories: string[];
  cacheVersion?: string;
  serverProductCount?: number;
  initialCategory?: string;
  basePath?: string;
  pageTitle?: string;
  pageDescription?: string;
  pageEyebrow?: string;
}
export default function ProductsClient({
  initialProducts: products,
  initialCategories: categories,
  initialCategory = "All",
  basePath = "/products",
  pageTitle = "Browse products",
  pageDescription = "Kitchenware, cookware and everyday essentials for homes and businesses in Al Ain.",
  pageEyebrow = "The Shop House collection",
}: Props) {
  const router = useRouter();
  const params = useSearchParams();
  const search = params.get("q") || "";
  const category = params.get("category") || initialCategory;
  const availability = params.get("availability") || "all";
  const sort = params.get("sort") || "recent";
  const min = params.get("min") || "";
  const max = params.get("max") || "";
  const [draft, setDraft] = useState(search);
  useEffect(() => {
    setDraft(search);
  }, [search]);
  const change = (updates: Record<string, string>) => {
    const next = new URLSearchParams(params.toString());
    next.delete("page");
    Object.entries(updates).forEach(([key, value]) => {
      if (!value || value === "All" || value === "all" || value === "recent")
        next.delete(key);
      else next.set(key, value);
    });
    router.push(`${basePath}${next.size ? `?${next}` : ""}`, { scroll: false });
  };
  const filtered = products
    .filter((product) => {
      return (
        productMatchesSearch(product, search) &&
        (category === "All" || formatCategory(product.category) === category) &&
        (availability === "all" ||
          (availability === "in" ? product.stock > 0 : product.stock <= 0)) &&
        (!min || product.discountedPrice >= Number(min)) &&
        (!max || product.discountedPrice <= Number(max))
      );
    })
    .sort((a, b) =>
      sort === "price-low"
        ? a.discountedPrice - b.discountedPrice
        : sort === "price-high"
          ? b.discountedPrice - a.discountedPrice
          : sort === "name"
            ? a.name.localeCompare(b.name)
            : Date.parse(b.createdAt) - Date.parse(a.createdAt),
    );
  const pages = Math.max(1, Math.ceil(filtered.length / 12));
  const requestedPage = Number(params.get("page")) || 1;
  const page = Math.min(pages, Math.max(1, Math.floor(requestedPage)));
  const visible = filtered.slice(0, page * 12);
  const requestedProduct = search.trim();
  const enquiryMessage = requestedProduct
    ? `Hello Shop House, I searched your website for "${requestedProduct}" but could not find the exact product. Do you have it available in your Al Ain store?`
    : "Hello Shop House, I could not find the product I need on the website. Can you help me check if it is available in your Al Ain store?";
  useEffect(() => {
    if (requestedProduct) {
      searchProductsGA(requestedProduct, filtered.length);
    }
  }, [requestedProduct, filtered.length]);
  const reset = () => {
    setDraft("");
    router.push(basePath, { scroll: false });
  };
  return (
    <div className="store-site">
      <Header />
      <main id="main-content">
        <section className="catalog-hero">
          <div className="catalog-hero-photo">
            <Image
              src="/instore2.webp"
              alt="Cookware displayed at Shop House"
              fill
              priority
              fetchPriority="high"
              quality={65}
              sizes="50vw"
            />
          </div>
          <div className="store-container">
            <p className="eyebrow">{pageEyebrow}</p>
            <h1 className="editorial-title">{pageTitle}</h1>
            <p>{pageDescription}</p>
          </div>
        </section>
        <div className="store-container catalog-content">
          <form
            className="catalog-search"
            onSubmit={(event) => {
              event.preventDefault();
              const query = draft.trim();
              router.push(query ? `/products?q=${encodeURIComponent(query)}` : "/products", { scroll: false });
            }}
          >
            <label htmlFor="catalog-search" className="sr-only">
              Search products
            </label>
            <Search size={22} aria-hidden="true" />
            <input
              id="catalog-search"
              type="search"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Search for products or categories…"
            />
            <button className="store-button primary" type="submit">
              Search
            </button>
          </form>
          <div className="category-pills" aria-label="Product categories">
            {categories.map((name) => (
              <Link
                key={name}
                aria-current={category === name ? "page" : undefined}
                href={name === "All" ? "/products" : categoryPath(name)}
              >
                {name}
              </Link>
            ))}
          </div>
          {filtered.length > 0 && (
            <aside className="catalog-enquiry" aria-label="Product enquiry">
              <div>
                <strong>
                  {requestedProduct
                    ? "Cannot see the exact product you need?"
                    : "Our website shows a selection of our range."}
                </strong>
                <p>
                  {requestedProduct
                    ? `We carry more items in our Al Ain store than are currently listed online. Ask us to check “${requestedProduct}”.`
                    : "More products are available in our Al Ain store. Send us a name or photo and we’ll check for you."}
                </p>
              </div>
              <WhatsAppLink
                label={requestedProduct ? "Ask us on WhatsApp" : "Ask about a product"}
                source="product_search_help"
                message={enquiryMessage}
              />
            </aside>
          )}
          <div className="catalog-layout">
            <aside className="catalog-filters" aria-label="Product filters">
              <div className="filter-heading">
                <h2>Filters</h2>
                <button onClick={reset}>Clear all</button>
              </div>
              <label htmlFor="category-filter">Category</label>
              <select
                id="category-filter"
                value={category}
                onChange={(event) =>
                  router.push(
                    event.target.value === "All"
                      ? "/products"
                      : categoryPath(event.target.value),
                    { scroll: false },
                  )
                }
              >
                {!categories.includes(category) && (
                  <option value={category}>{category}</option>
                )}
                {categories.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <fieldset>
                <legend>Availability</legend>
                {[
                  ["all", "All products"],
                  ["in", "In stock"],
                  ["out", "Out of stock"],
                ].map(([value, label]) => (
                  <label className="radio-label" key={value}>
                    <input
                      type="radio"
                      name="availability"
                      value={value}
                      checked={availability === value}
                      onChange={() => change({ availability: value })}
                    />
                    {label}
                  </label>
                ))}
              </fieldset>
              <fieldset>
                <legend>Price (AED)</legend>
                <div className="price-inputs">
                  <label>
                    <span className="sr-only">Minimum price</span>
                    <input
                      type="number"
                      min="0"
                      placeholder="Min"
                      value={min}
                      onChange={(event) => change({ min: event.target.value })}
                    />
                  </label>
                  <label>
                    <span className="sr-only">Maximum price</span>
                    <input
                      type="number"
                      min="0"
                      placeholder="Max"
                      value={max}
                      onChange={(event) => change({ max: event.target.value })}
                    />
                  </label>
                </div>
              </fieldset>
            </aside>
            <section aria-label="Products">
              <div className="catalog-results">
                <p aria-live="polite">
                  {filtered.length
                    ? `Showing 1–${visible.length} of ${filtered.length} products`
                    : "No matching products"}
                </p>
                <label>
                  Sort by
                  <select
                    value={sort}
                    onChange={(event) => change({ sort: event.target.value })}
                  >
                    <option value="recent">Newest</option>
                    <option value="price-low">Price: low to high</option>
                    <option value="price-high">Price: high to low</option>
                    <option value="name">Name</option>
                  </select>
                </label>
              </div>
              {visible.length ? (
                <div className="shop-product-grid">
                  {visible.map((product) => (
                    <ShopProductCard key={product.id} product={product} />
                  ))}
                  <aside className="catalog-browse-help">
                    <PackageSearch size={34} aria-hidden="true" />
                    <div>
                      <h2>Don’t see it online?</h2>
                      <p>
                        Our Al Ain store carries more products than this website
                        currently shows. Send us the product name or a photo and
                        we’ll check availability.
                      </p>
                    </div>
                    <WhatsAppLink
                      label="Ask about a product"
                      source="product_browse_help"
                      message="Hello Shop House, I am looking for a product that I could not find on your website. Can I send you the product name or a photo so you can check if it is available in your Al Ain store?"
                    />
                  </aside>
                </div>
              ) : (
                <div className="catalog-empty">
                  <h2>
                    {requestedProduct
                      ? `Looking for “${requestedProduct}”?`
                      : "No products found"}
                  </h2>
                  <p>
                    {requestedProduct
                      ? "It may still be available in our Al Ain store even though it is not listed online yet. Send us a message and our team will check for you."
                      : "Try another search or clear your filters. You can also message us to check our full in-store range."}
                  </p>
                  <div className="catalog-empty-actions">
                    <WhatsAppLink
                      label="Check store availability"
                      source="product_search_no_results"
                      message={enquiryMessage}
                    />
                    <button className="store-button outline" onClick={reset}>
                      Clear filters
                    </button>
                  </div>
                </div>
              )}
              {visible.length < filtered.length && (
                <div className="catalog-load-more">
                  <button
                    className="store-button outline"
                    onClick={() => change({ page: String(page + 1) })}
                  >
                    Load more products
                  </button>
                  <span>{filtered.length - visible.length} more available</span>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
